import {
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import clsx from "clsx";
import { useDebounce, useElementSize } from "usehooks-ts";

import { GenericTooltip } from "@components/tooltip-container";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
  useFocus,
  useInteractions,
  useListNavigation,
} from "@floating-ui/react";
import { GET_AD_OBJECT_NAMES } from "@gql/metadata";
import { useNorthbeamLazyQuery } from "@hooks/http";
import {
  type GetAdObjectNames,
  type GetAdObjectNamesVariables,
} from "@north-beam/nb-client-side";
import { TertiaryButton } from "@shared/buttons";
import Spinner from "@shared/spinner";

interface FilterQueryValue {
  level: string;
  query: string;
}

interface Props {
  label?: ReactNode;
  placeholder?: string;
  value: FilterQueryValue | null;
  className?: string;
  dropdownClassName?: string;
  levels?: string[];
  onChange: (value: FilterQueryValue | null) => void;
}

export const FilterQueryInput = ({
  label,
  placeholder,
  value,
  className,
  dropdownClassName,
  levels,
  onChange,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<
    {
      level: string;
      name: string;
    }[]
  >([]);
  const debouncedQuery = useDebounce(query, 500);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<(HTMLDivElement | null)[]>([]);

  const [setInputContainerRef, { width: inputContainerWidth }] =
    useElementSize();

  const {
    data: { loading },
    loadData,
  } = useNorthbeamLazyQuery<GetAdObjectNames, GetAdObjectNamesVariables>(
    GET_AD_OBJECT_NAMES,
    {
      onCompleted(data) {
        setSuggestions(
          data?.me.adObjectNames.map(({ level, name }) => ({ level, name })) ??
            [],
        );
      },
    },
  );

  useEffect(() => {
    loadData({ variables: { searchQuery: debouncedQuery, levels } });
  }, [loadData, debouncedQuery, levels]);

  useEffect(() => {
    setQuery(value ? value.query : "");
  }, [value, hasFocus]);

  const { refs, floatingStyles, context } = useFloating({
    transform: false,
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [flip(), shift(), offset(4)],
  });

  const focus = useFocus(context);

  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    virtual: true,
    focusItemOnOpen: true,
    onNavigate: setActiveIndex,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [focus, listNavigation],
  );

  const selectItem = () => {
    if (activeIndex !== null) {
      const selectedItem = suggestions[activeIndex];
      onChange(
        selectedItem
          ? { level: selectedItem.level, query: selectedItem.name }
          : null,
      );
      setQuery(selectedItem ? selectedItem.name : "");
    } else {
      setQuery("");
    }
  };

  return (
    <div className={className}>
      <div className="nb-input-container">
        {label ? label : <label className="label">SEARCH</label>}
        <div
          ref={(node) => {
            setInputContainerRef(node);
            refs.setReference(node);
          }}
          className={clsx(
            "nb-input bg-v2-primary border border-v2-secondary hover:border-v2-action group/input flex items-center py-0 pl-2 pr-1 cursor-text",
            {
              "shadow-[0_0_0_1px_#7081ff] border-v2-action": hasFocus,
            },
          )}
          tabIndex={0}
          {...getReferenceProps({
            onFocus: () => {
              inputRef.current?.focus();
            },
          })}
        >
          {value && (
            <span className="py-0.5 px-2.5 rounded capitalize bg-gray-2 text-xs font-normal">
              {value.level}
            </span>
          )}
          <input
            ref={inputRef}
            className="bg-v2-primary group-hover/input:bg-v2-secondary h-full w-full pl-1 rounded-lg"
            placeholder={
              value
                ? ""
                : placeholder ??
                  "Search by name, campaign id, adset id, ad id..."
            }
            value={query}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setActiveIndex(0);
              setQuery(e.target.value);
            }}
            onFocus={() => {
              setActiveIndex(0);
              setHasFocus(true);
            }}
            onBlur={() => {
              setActiveIndex(null);
              setHasFocus(false);
            }}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (["Escape", "Enter", "Tab"].includes(e.key)) {
                selectItem();
                e.currentTarget.blur();
              }
            }}
          />
          {value ? (
            <TertiaryButton
              className={clsx(
                "shrink-0 w-6 h-6 p-0 ml-1 text-base flex items-center justify-center",
                "rounded bg-transparent hover:bg-[#fce2db]",
              )}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={() => {
                setTimeout(() => {
                  onChange(null);
                }, 10);
                inputRef.current?.blur();
              }}
            >
              <i className="fa-regular fa-sharp fa-times text-mars"></i>
            </TertiaryButton>
          ) : (
            <i className="fa-regular fa-sharp text-xs fa-magnifying-glass mr-1.5"></i>
          )}
        </div>
      </div>
      <FloatingPortal>
        {isOpen && (
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, width: inputContainerWidth }}
            className={clsx(
              "bg-v2-primary text-v2-primary rounded-lg elevation-1 text-xs px-0 py-1 min-w-[360px]",
              "max-h-[400px] overflow-auto",
              dropdownClassName,
              { "min-h-[150px] flex items-center justify-center": loading },
            )}
            {...getFloatingProps({
              onMouseDown: (e) => {
                e.preventDefault();
              },
            })}
          >
            {loading && (
              <div className="flex items-center justify-center">
                <Spinner colorClassname="text-cosmic mb-1" />
              </div>
            )}
            {!loading && suggestions.length === 0 && (
              <div className="px-4 py-2 text-disabled">NO RESULTS FOUND</div>
            )}
            {!loading && suggestions.length !== 0 && (
              <>
                {suggestions.map((row, index) => (
                  <div
                    key={JSON.stringify(row)}
                    className={clsx(
                      "px-4 h-8 cursor-pointer flex items-center gap-2 truncate",
                      { "bg-v2-secondary font-bold": activeIndex === index },
                    )}
                    tabIndex={activeIndex === index ? 0 : -1}
                    ref={(node) => {
                      listRef.current[index] = node;
                    }}
                    {...getItemProps({
                      onClick: () => {
                        selectItem();
                        inputRef.current?.blur();
                        setIsOpen(false);
                      },
                    })}
                  >
                    <span className="py-0.5 px-2.5 rounded capitalize bg-v2-action-tertiary font-normal">
                      {row.level}
                    </span>
                    <GenericTooltip
                      noInfoCircle
                      content={row.name}
                      placement="right"
                      className="truncate"
                    >
                      <span className="truncate">{row.name}</span>
                    </GenericTooltip>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </FloatingPortal>
    </div>
  );
};
